import { NO_PARENT } from '@tunasong/schemas'

/**
 * Upper bound on how far a parent-chain walk will climb.
 *
 * A bound is required, not defensive polish: the walk advances via the loaded
 * entity's `parentId`, so any state where that fails to advance — a missing
 * entity, or a cycle in the parentId graph — loops forever. On the client that
 * freezes the tab (GH #1753).
 */
export const MAX_PARENT_CHAIN_DEPTH = 50

/** Why a walk ended. Callers decide which of these are errors for them. */
export type ParentChainStop = 'root' | 'unreadable' | 'cycle' | 'max-depth'

export interface ParentChainResult<T> {
  /** Entities from the start id upward, nearest first. */
  chain: T[]
  stoppedBecause: ParentChainStop
}

export interface WalkParentChainOptions<T> {
  /** Id to start from — typically the entity's `parentId`. */
  startId: string
  /**
   * Load one entity by id. Resolve `undefined` for anything the caller cannot
   * or should not read (a 403, a missing row); the walk then truncates and
   * reports `'unreadable'` rather than throwing.
   */
  load: (id: string) => Promise<T | undefined>
  /** @default MAX_PARENT_CHAIN_DEPTH */
  maxDepth?: number
}

/**
 * Walk an entity's ancestors, nearest first, stopping at the root.
 *
 * Single definition shared by the server-side `get_parent_chain` tool and the
 * client-side `useAncestors` hook. They previously each implemented this, and
 * only the server version carried the cycle and depth guards — which is how the
 * client came to hang on inputs the server handled fine.
 *
 * Truncation is always graceful: an unreadable ancestor, a cycle, or the depth
 * cap ends the walk and returns what was reachable, with `stoppedBecause` saying
 * which. A caller that needs to treat a specific stop as an error (the agent
 * tool errors when the immediate parent is unreadable) checks the reason and the
 * chain length itself.
 */
export const walkParentChain = async <T extends { parentId?: string | null }>({
  startId,
  load,
  maxDepth = MAX_PARENT_CHAIN_DEPTH,
}: WalkParentChainOptions<T>): Promise<ParentChainResult<T>> => {
  const chain: T[] = []
  /** Ids already requested — the only thing standing between us and a cycle. */
  const seen = new Set<string>()
  let currentId: string | null | undefined = startId

  while (currentId && currentId !== NO_PARENT) {
    if (seen.has(currentId)) {
      return { chain, stoppedBecause: 'cycle' }
    }
    if (chain.length >= maxDepth) {
      return { chain, stoppedBecause: 'max-depth' }
    }
    seen.add(currentId)

    const entity = await load(currentId)
    if (!entity) {
      return { chain, stoppedBecause: 'unreadable' }
    }

    chain.push(entity)
    currentId = entity.parentId
  }

  return { chain, stoppedBecause: 'root' }
}
